/**
 * Vehicle statistics computed from stored GPS history points.
 * Point shape: { lat, lng, s (speed kph), t (epoch ms) }  (see gpsTrack/gpsTrackDb)
 *
 * Notes on accuracy: trackers here report position + speed but NO ignition/ACC or
 * fuel sensor, so "idle / engine working / engine hours / fuel" are ESTIMATES
 * derived from speed & time (clearly flagged in the UI).
 */

const MOVING_KPH = 5;          // at/below this = stopped
const MIN_STOP_MS = 3 * 60000; // a "stop" must last ≥ 3 min to count
const MAX_GAP_MS = 60 * 60000; // ignore time gaps > 1h (device offline) for durations

const hav = (lat1, lon1, lat2, lon2) => {
  if (lat1 == null || lat2 == null) return 0;
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/** Format milliseconds → "Hh Mm" (or "Mm"). */
export const fmtDuration = (ms) => {
  if (!ms || ms < 0) ms = 0;
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

/**
 * Preset date ranges. Returns { sinceMs, untilMs } in epoch ms (local boundaries).
 */
export const getPresetRange = (key, now = new Date()) => {
  const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  const dayMs = 86400000;
  const today0 = startOfDay(now).getTime();
  const nowMs = now.getTime();
  switch (key) {
    case 'today': return { sinceMs: today0, untilMs: nowMs };
    case 'yesterday': return { sinceMs: today0 - dayMs, untilMs: today0 };
    case 'last2': return { sinceMs: nowMs - 2 * dayMs, untilMs: nowMs };
    case 'last3': return { sinceMs: nowMs - 3 * dayMs, untilMs: nowMs };
    case 'week': {
      const d = startOfDay(now);
      const dow = (d.getDay() + 6) % 7; // Monday = 0
      return { sinceMs: d.getTime() - dow * dayMs, untilMs: nowMs };
    }
    case 'lastweek': {
      const d = startOfDay(now);
      const dow = (d.getDay() + 6) % 7;
      const thisMonday = d.getTime() - dow * dayMs;
      return { sinceMs: thisMonday - 7 * dayMs, untilMs: thisMonday };
    }
    case 'month': {
      const d = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
      return { sinceMs: d, untilMs: nowMs };
    }
    case 'lastmonth': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
      const end = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
      return { sinceMs: start, untilMs: end };
    }
    default: return { sinceMs: today0, untilMs: nowMs };
  }
};

/**
 * Compute statistics + map overlays from an ordered list of points.
 * @param {Array} points - sorted ascending by t
 * @param {{ speedLimitKph?: number, car?: object, settings?: object }} opts
 */
export const computeStats = (points = [], opts = {}) => {
  const speedLimit = opts.speedLimitKph || 120;
  const empty = {
    hasData: false,
    startTime: null, endTime: null,
    distanceKm: 0, movingMs: 0, stopMs: 0, idleMs: 0, workingMs: 0, engineMs: 0,
    stopsCount: 0, maxSpeed: 0, avgSpeed: 0, overspeedCount: 0,
    stops: [], overspeed: [], path: []
  };
  if (!points || points.length === 0) return empty;

  const path = points.map(p => [p.lat, p.lng]);
  let distanceKm = 0, movingMs = 0, stopMs = 0, maxSpeed = 0;
  const stops = [];
  const overspeed = [];

  let curStop = null;       // { lat, lng, start, end }
  let prevOver = false;

  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const sp = Number(p.s) || 0;
    if (sp > maxSpeed) maxSpeed = sp;

    // overspeed crossing (rising edge)
    const over = sp > speedLimit;
    if (over && !prevOver) overspeed.push({ lat: p.lat, lng: p.lng, t: p.t, speed: sp });
    prevOver = over;

    if (i > 0) {
      const a = points[i - 1];
      let dt = p.t - a.t;
      if (dt < 0) dt = 0;
      const seg = hav(a.lat, a.lng, p.lat, p.lng);
      if (seg < 500) distanceKm += seg; // ignore teleport noise
      if (dt <= MAX_GAP_MS) {
        if ((Number(a.s) || 0) > MOVING_KPH) movingMs += dt; else stopMs += dt;
      }
    }

    // stop segmentation
    if (sp <= MOVING_KPH) {
      if (!curStop) curStop = { lat: p.lat, lng: p.lng, start: p.t, end: p.t };
      else curStop.end = p.t;
    } else if (curStop) {
      if (curStop.end - curStop.start >= MIN_STOP_MS) stops.push({ ...curStop });
      curStop = null;
    }
  }
  if (curStop && curStop.end - curStop.start >= MIN_STOP_MS) stops.push({ ...curStop });

  const startTime = points[0].t;
  const endTime = points[points.length - 1].t;
  const movingHours = movingMs / 3600000;
  const avgSpeed = movingHours > 0 ? distanceKm / movingHours : 0;

  // Estimates (no ignition signal): idle ≈ stop time, working ≈ moving, engine ≈ both.
  const idleMs = stopMs;
  const workingMs = movingMs;
  const engineMs = movingMs + stopMs;

  // Optional fuel (only if configured on the car / settings)
  const car = opts.car || {};
  const settings = opts.settings || {};
  const rate = parseFloat(car.fuelConsumption); // L/100km
  const price = parseFloat(settings.fuelPrice); // currency / L
  let fuelLiters = null, fuelCost = null;
  if (rate > 0) {
    fuelLiters = (distanceKm * rate) / 100;
    if (price > 0) fuelCost = fuelLiters * price;
  }

  return {
    hasData: true,
    startTime, endTime,
    distanceKm,
    movingMs, stopMs, idleMs, workingMs, engineMs,
    stopsCount: stops.length,
    maxSpeed: Math.round(maxSpeed),
    avgSpeed,
    overspeedCount: overspeed.length,
    fuelLiters, fuelCost,
    stops, overspeed, path
  };
};
